import React from 'react';
import { seriesSlope, isFlatSeries, isNetFlat } from './formatAmount.js';

/* v8 Path K — MiniSparkline
   Shared sparkline primitive used across FND. Extracted from CMA's ratio
   grid so every metric that earns a trend-at-a-glance (ratios, KPI rows,
   drill-down tables, future metrics) shares the same rendering + tone
   logic. One component, everywhere.

   Inputs:
   - series: Array<number | null | undefined> (drops non-finite gracefully)
   - tone: 'good' | 'bad' | 'warn' | 'neutral' (picks stroke color)
   - gradient: boolean — when true, fills area under the line with a
                       soft gradient (wired to the ScenarioBar's global
                       "Spark gradient" toggle)
   - size: 'xs' | 'sm' | 'md' — preset sizes. 'xs' = 40×10 (dense tables),
                                'sm' = 56×14 (ratio grid), 'md' = 80×20.
   - highlightIdx: index to draw a dot on (default: middle if present);
                  typically "now" in a past-through-future series.
*/
const SIZES = {
  xs: { w: 40, h: 10, pad: 1, stroke: 1.1, dot: 1.4 },
  sm: { w: 56, h: 14, pad: 1, stroke: 1.25, dot: 1.8 },
  md: { w: 80, h: 20, pad: 1.5, stroke: 1.5, dot: 2.2 },
};

const TONE_STROKE = {
  good:    '#4ade80',
  bad:     '#f87171',
  warn:    '#f0b429',
  neutral: '#8b93a7',
  up:      '#4ade80',
  down:    '#f87171',
};

export default function MiniSparkline({
  series,
  tone = 'neutral',
  gradient = false,
  size = 'sm',
  highlightIdx,
  ariaLabel,
  className = '',
}) {
  const { w, h, pad, stroke: sw, dot: dr } = SIZES[size] || SIZES.sm;
  const pts = (series || []).map((v, i) => ({ i, v }));
  const valid = pts.filter(p => Number.isFinite(p.v));
  if (valid.length < 2) {
    return <span className={`text-[0.7rem] text-text-dim ${className}`}>—</span>;
  }
  const vals = valid.map(p => p.v);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const xStep = (w - 2 * pad) / Math.max(1, pts.length - 1);
  const stroke = TONE_STROKE[tone] || TONE_STROKE.neutral;

  const toXY = ({ i, v }) => {
    const x = pad + i * xStep;
    const y = pad + (1 - (v - min) / range) * (h - 2 * pad);
    return { x, y };
  };

  const linePath = valid
    .map((p, idx) => {
      const { x, y } = toXY(p);
      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  /* Gradient fill — close the path down to the baseline (bottom of the
     viewbox) so the fill is bounded. Only rendered when gradient=true. */
  const areaPath = gradient
    ? linePath +
      ` L${(pad + valid[valid.length - 1].i * xStep).toFixed(2)},${(h - pad).toFixed(2)}` +
      ` L${(pad + valid[0].i * xStep).toFixed(2)},${(h - pad).toFixed(2)} Z`
    : null;

  /* Highlight dot — default = "now" = middle index if present, else last. */
  const hIdx = highlightIdx != null
    ? highlightIdx
    : Math.floor(pts.length / 2);
  const hPoint = valid.find(p => p.i === hIdx) || valid[valid.length - 1];
  const { x: hx, y: hy } = toXY(hPoint);

  const gradId = `msg-${tone}-${React.useId().replace(/:/g, '')}`;

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className={`shrink-0 ${className}`}
      aria-label={ariaLabel || 'trend sparkline'}
      role="img"
    >
      {gradient && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {gradient && areaPath && <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />}
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={hx} cy={hy} r={dr} fill={stroke} />
    </svg>
  );
}

/* v8 Path K — Delta badge (↑/↓/→) computed from a series. Pairs nicely
   with MiniSparkline in a flex row. Pure render, no state. */
/* SparklineDeltaBadge — the small ▲ / ▼ / → glyph next to sparklines.
   Its job is to be **the final verdict** — an unambiguous direction read
   that matches exactly what the user sees in the sparkline itself.

   Per the app-wide rule (2026-04-22, refined):

     - ARROW MATCHES THE VISUAL SPARKLINE. The series the sparkline
       plots is the series the arrow reads. When an expense row is
       rendered in "normal balance mode" (parens-convention flips the
       negative stored value to a positive magnitude for display), both
       the sparkline AND the arrow see the magnitude. So if the
       magnitude of an expense grows (spending MORE), the sparkline
       slopes up and the arrow points ▲. If the magnitude shrinks, both
       go down.
     - ARROW DOES NOT REACT TO SEMANTIC TONE. Direction and color come
       from the raw slope sign of the displayed series. No polarity
       lookup, no favorability flip. The sparkline LINE color may flip
       when Semantic Tone toggles (via the `tone` prop on MiniSparkline)
       — but this arrow glyph stays locked.
         slope > 0  → ▲ green
         slope < 0  → ▼ red
         flat       → → grey
     - The `tone` prop here is intentionally ignored for color. Kept on
       the API only so existing callers don't break.

   Props:
     - series     : Array<number>   — the same array the sparkline plots
     - rawSeries  : deprecated, ignored (kept for API compat)
     - tone       : ignored for color (kept for API compat)
     - format     : 'currency' | 'pct' | 'days' | 'months' | 'x' | 'ratio'
*/
export function SparklineDeltaBadge({ series, rawSeries: _rawSeries, format = 'ratio', tone: _tone }) {
  if (!series || series.length < 2) return null;
  const valid = series.filter(v => Number.isFinite(v));
  if (valid.length < 2) return null;
  const first = valid[0];
  const last = valid[valid.length - 1];
  const delta = last - first;

  /* Arrow uses the SAME series the sparkline plots — normal-balance
     magnitude view, matching what the user actually sees.

     NET-flat rule: if the start and end values are within materiality
     threshold (per `isNetFlat`), the arrow is → grey — even when the
     series visibly wobbled mid-flight. Intent: the final verdict
     compares where you ended up against where you started, not how
     dramatic the journey was. A stock that closed at $100 after ranging
     $95–$120 that day "ended flat" for verdict purposes.

     We OR isNetFlat with isFlatSeries so a strictly-flat series also
     reads as → grey (both checks agree in that case anyway). */
  const slope = seriesSlope(series);
  const flat  = isNetFlat(series, format) || isFlatSeries(series, format);
  const sym   = flat ? '→' : slope > 0 ? '▲' : '▼';
  const cls   = flat
    ? 'text-text-dim'
    : slope > 0
      ? 'text-accent-green'
      : 'text-accent-red';

  const pct = first !== 0 ? delta / Math.abs(first) : 0;
  const label = format === 'pct'
    ? `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}pp`
    : format === 'days' || format === 'months'
    ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`
    : `${(pct * 100 >= 0 ? '+' : '') + (pct * 100).toFixed(0)}%`;
  return (
    <span
      className={`text-[0.65rem] font-mono tabular-nums ${cls}`}
      title={`Series trend: ${sym} ${label}`}
    >
      {sym}
    </span>
  );
}
